<template>
  <modal :allowCloseBtn="true" @close="closeModal">
    <tile>
      <step>
        <template v-slot:header> How Much Do You Need for Your Selected Term? </template>
        <p>
          These funds can be used for your school's total cost of attendance which typically
          includes tuition, fees, books, room & board, food & transportation.
        </p>
        <credit-availability-notice />
        <div class="estimate-calculator">
          <div class="description">
            <p>
              Amount for
              <b>
                {{ selectedTerm }}
              </b>

              {{ combinedTerms ? "combined" : "" }}
            </p>

            <a @click="toggleCalculator">How much do I need?</a>
          </div>
          <div class="calculator-tooltip" :class="{ open: displayEstimateCalculator }">
            <estimate-calculator
              :term="term || ''"
              :application="application"
              @useThisAmount="useThisAmount($event)"
              @close="toggleCalculator"
            />
          </div>
        </div>
        <BorrowAmountLOC ref="borrowAmountRef" :disabled="loading" />
        <div class="hint">(If you are unsure, enter 0.)</div>
        <div v-if="disbursements.length > 0" class="estimated-disbursements">
          <p>Estimated disbursements to be sent to your school:</p>
          <div class="disbursements">
            <div v-for="(disbursement, index) in disbursements" :key="index" class="disbursement">
              <p class="amount">${{ formatNumber(disbursement.amount) }}</p>
              <p class="term">{{ disbursement.term }}</p>
            </div>
          </div>
        </div>
        <div class="info-tile">
          <icon icon="info" />
          <p>
            You can use this line of credit over multiple years
            <b>without</b> reapplying each year
          </p>
          <p>
            Your maximum line of credit for your {{ studentType }} degree is
            <b>${{ formatNumber(appData?.application?.loanAmount) }}</b>
          </p>
        </div>
        <template v-slot:actions>
          <next-button
            :loading="loading"
            :disabled="saveChangesDisabled"
            @click="goBackToPortal"
            text="Save Changes"
          />
        </template>
      </step>
    </tile>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import useFormatter from "@/use/formatter";
import Icon from "@/components/shared/Icon.vue";
import Step from "@/components/shared/Step.vue";
import EstimateCalculator from "./EstimateCalculator.vue";
import Tile from "@/components/shared/Tile.vue";
import NextButton from "@/components/shared/NextButton.vue";
import Modal from "@/components/shared/Modal.vue";
import CreditAvailabilityNotice from "@/components/shared/CreditAvailabilityNotice.vue";
import { useStore } from "vuex";
import BorrowAmountLOC from "./BorrowAmountLOC.vue";
import useApplicationData from "@/composables/useApplicationData";

const store = useStore();

const { studentType } = useApplicationData();

// Template Refs
const borrowAmountRef = ref<InstanceType<typeof BorrowAmountLOC>>();

// Props
interface Props {
  application: any;
  term?: string;
  initialDrawRequest?: any;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  term: undefined,
  initialDrawRequest: undefined,
  loading: false,
});

// Emit
const emit = defineEmits(["previous", "close", "complete"]);

const { formatNumber } = useFormatter();

// Refs
const displayEstimateCalculator = ref<boolean>(false);

// Computed
const amount = computed<number>(() => {
  return borrowAmountRef.value?.values?.amount;
});

const saveChangesDisabled = computed(() => {
  return borrowAmountRef.value?.hasErrors || props.loading;
});

const appData = computed(() => store.state.appData);

const selectedTerm = computed<string>(() => {
  return store?.state?.appData?.application?.schoolConfiguration?.academicTerm
    ? store?.state?.appData?.application?.schoolConfiguration?.academicTerm
    : "Not Yet Selected";
});

const combinedTerms = computed<boolean>(() => {
  return (store?.state?.appData?.application?.schoolConfiguration?.academicTerm || "").includes(
    "-",
  );
});

const disbursements = computed(() => {
  const terms = props.term?.split("-") || [];
  return terms.map((term: string) => {
    return {
      term: term.trim(),
      amount: (amount.value || 0) / (terms || []).length,
    };
  });
});

const terms = computed<string>(() => {
  const terms = props.term?.split("-") || [];
  let termString = "";
  for (let i = 0; i < terms.length; i++) {
    if (i === 0) {
      termString += terms[i].trim();
    } else if (i > 0 && i < terms.length - 1) {
      termString += `, ${terms[i].trim()}`;
    } else if (i === terms.length - 1) {
      termString += ` and ${terms[i].trim()}`;
    }
  }
  return termString;
});

const requestedAmount = computed<number | null>(() => {
  if (amount.value === undefined || amount.value === null || isNaN(Number(amount.value))) {
    return null;
  }
  return Number(amount.value);
});

const requestedAmountChanged = computed<boolean>(() => {
  const original = store.state.loanConfigurationModule?.applicationInfo?.loanAmount;

  if (original === requestedAmount.value) {
    return false;
  }

  return true;
});

// Methods
function useThisAmount(event: string | number | undefined) {
  borrowAmountRef.value?.updateAmount(event);
}

function setReqAmount() {
  borrowAmountRef.value?.updateAmount(props.initialDrawRequest?.amount || undefined, false);
}

function closeModal() {
  emit("close", true);
}

function toggleCalculator() {
  displayEstimateCalculator.value = !displayEstimateCalculator.value;
}

function goBackToPortal() {
  emit("complete", { amount: amount.value, requestedAmountChanged: requestedAmountChanged.value });
}

onMounted(() => {
  setReqAmount();
});

defineExpose({
  amount,
  terms,
});
</script>
<style lang="scss" scoped>
.estimate-calculator {
  .description {
    display: flex;
    justify-content: space-between;
    align-items: center;

    p {
      margin: 0;
      width: 65%;
    }

    a {
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .calculator-tooltip {
    display: none;
    opacity: 0;
    border-radius: 6px;
    border: 1px solid lightgray;
    transition: opacity 0.6s;
    margin: 1em 0;
    padding: 0 1.5em 1em;
    box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);
    -webkit-box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);
    -moz-box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);

    &.open {
      display: block;
      opacity: 1;
    }
  }
}

.hint {
  font-size: 0.8em;
  font-weight: 400;
  color: #767676;
  font-style: italic;
}

.estimated-disbursements {
  margin-top: 20px;

  p {
    margin: 0;
  }

  .disbursements {
    display: flex;
    padding: 1em 0;

    .disbursement {
      padding: 0 15px;

      &:not(:first-child) {
        border-left: 1px solid lightgray;
      }

      .amount {
        font-size: 1.4em;
        font-weight: 700;
      }

      .term {
        font-size: 0.8em;
      }
    }
  }
}

.info-tile {
  margin: 1.5em 0;
  background: #e7e7e7;
  padding: 1.5em;
  border-radius: 6px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);
  -webkit-box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);
  -moz-box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);
  color: #767676;

  b {
    color: #000;
  }

  .icon {
    color: #000;
    font-size: 25px;
  }

  a {
    color: #000;
    cursor: pointer;
    text-decoration: underline;
  }
}

.btn {
  display: inline-block;
  text-align: center;
  vertical-align: middle;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 600;
  line-height: 1.5em;
  white-space: normal;
  padding: 0.715em 0.841em;
  cursor: pointer;

  :nth-last-child(n + 2):nth-last-child(-n + 2):first-child {
    margin-right: 20px;
  }

  &:focus,
  &:hover {
    filter: brightness(90%) saturate(90%);
  }

  &.btn-primary {
    background: var(--theme-color);
    color: #fff;
    min-width: 150px;
    max-width: 100%;
  }

  &:disabled {
    opacity: 0.65;
    cursor: default;

    &:focus,
    &:hover {
      filter: none;
    }
  }
}
</style>
